// Vues complètes pour le tableau de bord agence
import React, { useState } from 'react';
import {
    Calendar as CalendarIcon, Check, X, Edit, Trash2, Eye, Send, Download,
    Filter, Search, ChevronLeft, ChevronRight, Clock, MapPin, Phone, Mail,
    User, DollarSign, FileText, MessageSquare, Star, AlertCircle, CheckCircle,
    XCircle, TrendingUp, BarChart3, PieChart as PieChartIcon, Settings, Upload,
    Save, RefreshCw, Bell, BellOff
} from 'lucide-react';

// ==================== VUE RÉSERVATIONS ====================
export const ReservationsView = ({ reservations, filters, setFilters, setSelectedReservation, updateReservationStatus }) => {
    const [selectedTab, setSelectedTab] = useState('all');

    const tabs = [
        { id: 'all', label: 'Toutes', count: reservations.length },
        { id: 'pending', label: 'En attente', count: reservations.filter(r => r.status === 'pending').length },
        { id: 'confirmed', label: 'Confirmées', count: reservations.filter(r => r.status === 'confirmed').length },
        { id: 'active', label: 'Actives', count: reservations.filter(r => r.status === 'active').length },
        { id: 'completed', label: 'Terminées', count: reservations.filter(r => r.status === 'completed').length }
    ];

    const filteredReservations = selectedTab === 'all'
        ? reservations
        : reservations.filter(r => r.status === selectedTab);

    const handleStatusChange = async (reservationId, newStatus) => {
        const result = await updateReservationStatus(reservationId, newStatus);
        if (!result.success) {
            alert(result.error);
        }
    };

    return (
        <div className="space-y-6">
            {/* En-tête */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white">Réservations</h2>
                    <p className="text-slate-300">Gérer les demandes de location</p>
                </div>

                <div className="flex gap-2">
                    <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
                        <Download className="h-4 w-4" />
                        Exporter
                    </button>
                </div>
            </div>

            {/* Onglets de filtrage */}
            <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setSelectedTab(tab.id)}
                        className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all
              ${selectedTab === tab.id
                                ? 'bg-indigo-600 text-white'
                                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                            }`}
                    >
                        {tab.label} ({tab.count})
                    </button>
                ))}
            </div>

            {/* Filtres et recherche */}
            <div className="bg-slate-800/50 rounded-xl p-4 flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Rechercher par client, véhicule..."
                            value={filters.searchQuery}
                            onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                            className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-lg pl-10 pr-4 py-2"
                        />
                    </div>
                </div>

                <select
                    value={filters.dateRange}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                    className="bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-4 py-2"
                >
                    <option value="all">Toutes les dates</option>
                    <option value="today">Aujourd'hui</option>
                    <option value="week">Cette semaine</option>
                    <option value="month">Ce mois</option>
                </select>
            </div>

            {/* Liste des réservations */}
            <div className="space-y-4">
                {filteredReservations.map(reservation => (
                    <div key={reservation.id} className="bg-gradient-to-r from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-xl p-6 hover:shadow-xl transition-all">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            {/* Informations principales */}
                            <div className="flex-1 space-y-3">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <h3 className="text-white font-semibold text-lg">{reservation.client_name}</h3>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium
                    ${reservation.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                                            reservation.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                                reservation.status === 'active' ? 'bg-blue-500/20 text-blue-400' :
                                                    'bg-slate-500/20 text-slate-400'
                                        }`}>
                                        {reservation.status === 'confirmed' ? '✅ Confirmée' :
                                            reservation.status === 'pending' ? '⏳ En attente' :
                                                reservation.status === 'active' ? '🚗 Active' : '✓ Terminée'}
                                    </span>
                                    {reservation.payment_status === 'pending' && (
                                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                                            💳 Paiement en attente
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <CalendarIcon className="h-4 w-4 text-blue-400" />
                                        <span>{new Date(reservation.pickup_date).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <Clock className="h-4 w-4 text-purple-400" />
                                        <span>{reservation.duration} jours</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <MapPin className="h-4 w-4 text-emerald-400" />
                                        <span>{reservation.pickup_location}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <DollarSign className="h-4 w-4 text-amber-400" />
                                        <span className="font-bold text-emerald-400">{reservation.amount?.toLocaleString()} DZD</span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 text-slate-300">
                                    <span className="font-medium">Véhicule:</span>
                                    <span>{reservation.vehicle}</span>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-2">
                                {reservation.status === 'pending' && (
                                    <>
                                        <button
                                            onClick={() => handleStatusChange(reservation.id, 'confirmed')}
                                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                                        >
                                            <Check className="h-4 w-4" />
                                            Accepter
                                        </button>
                                        <button
                                            onClick={() => handleStatusChange(reservation.id, 'cancelled')}
                                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                                        >
                                            <X className="h-4 w-4" />
                                            Refuser
                                        </button>
                                    </>
                                )}

                                <button
                                    onClick={() => setSelectedReservation(reservation)}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                                >
                                    <Eye className="h-4 w-4" />
                                    Détails
                                </button>

                                <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors">
                                    <MessageSquare className="h-4 w-4" />
                                    Contacter
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                {filteredReservations.length === 0 && (
                    <div className="text-center py-16 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                        <CalendarIcon className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                        <h3 className="text-white text-lg font-semibold mb-2">Aucune réservation</h3>
                        <p className="text-slate-400">Les réservations apparaîtront ici</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==================== VUE CALENDRIER ====================
export const CalendarView = ({ reservations, vehicles }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedVehicle, setSelectedVehicle] = useState('all');
    const [viewMode, setViewMode] = useState('month'); // month, week, day

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        return { daysInMonth, startingDayOfWeek, year, month };
    };

    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

    const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

    const previousMonth = () => {
        setCurrentDate(new Date(year, month - 1, 1));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(year, month + 1, 1));
    };

    const getReservationsForDay = (day) => {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return reservations.filter(r => {
            const pickupDate = new Date(r.pickup_date).toISOString().split('T')[0];
            const returnDate = new Date(r.return_date).toISOString().split('T')[0];
            return dateStr >= pickupDate && dateStr <= returnDate;
        });
    };

    return (
        <div className="space-y-6">
            {/* En-tête */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white">Calendrier & Planning</h2>
                    <p className="text-slate-300">Vue d'ensemble des réservations</p>
                </div>

                <div className="flex gap-2">
                    <select
                        value={selectedVehicle}
                        onChange={(e) => setSelectedVehicle(e.target.value)}
                        className="bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-4 py-2"
                    >
                        <option value="all">Tous les véhicules</option>
                        {vehicles.map(v => (
                            <option key={v.id} value={v.id}>{v.make} {v.model}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Navigation du calendrier */}
            <div className="bg-slate-800/50 rounded-xl p-4 flex items-center justify-between">
                <button
                    onClick={previousMonth}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                    <ChevronLeft className="h-5 w-5 text-white" />
                </button>

                <h3 className="text-xl font-bold text-white">
                    {monthNames[month]} {year}
                </h3>

                <button
                    onClick={nextMonth}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                    <ChevronRight className="h-5 w-5 text-white" />
                </button>
            </div>

            {/* Grille du calendrier */}
            <div className="bg-slate-800/50 rounded-xl p-4">
                {/* Jours de la semaine */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                    {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
                        <div key={day} className="text-center text-slate-400 font-medium text-sm py-2">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Jours du mois */}
                <div className="grid grid-cols-7 gap-2">
                    {/* Jours vides avant le début du mois */}
                    {[...Array(startingDayOfWeek)].map((_, i) => (
                        <div key={`empty-${i}`} className="aspect-square"></div>
                    ))}

                    {/* Jours du mois */}
                    {[...Array(daysInMonth)].map((_, i) => {
                        const day = i + 1;
                        const dayReservations = getReservationsForDay(day);
                        const isToday = new Date().getDate() === day &&
                            new Date().getMonth() === month &&
                            new Date().getFullYear() === year;

                        return (
                            <div
                                key={day}
                                className={`aspect-square border border-slate-600/50 rounded-lg p-2 hover:bg-slate-700/50 transition-colors cursor-pointer
                  ${isToday ? 'bg-indigo-600/20 border-indigo-500' : 'bg-slate-700/30'}`}
                            >
                                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-indigo-400' : 'text-white'}`}>
                                    {day}
                                </div>
                                <div className="space-y-1">
                                    {dayReservations.slice(0, 2).map(res => (
                                        <div
                                            key={res.id}
                                            className={`text-xs px-1 py-0.5 rounded truncate
                        ${res.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                                                    res.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                                        'bg-blue-500/20 text-blue-400'
                                                }`}
                                            title={`${res.client_name} - ${res.vehicle}`}
                                        >
                                            {res.client_name}
                                        </div>
                                    ))}
                                    {dayReservations.length > 2 && (
                                        <div className="text-xs text-slate-400">
                                            +{dayReservations.length - 2} autres
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Légende */}
            <div className="bg-slate-800/50 rounded-xl p-4">
                <h4 className="text-white font-medium mb-3">Légende</h4>
                <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-emerald-500/20 border border-emerald-400/30"></div>
                        <span className="text-slate-300 text-sm">Confirmée</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-amber-500/20 border border-amber-400/30"></div>
                        <span className="text-slate-300 text-sm">En attente</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-blue-500/20 border border-blue-400/30"></div>
                        <span className="text-slate-300 text-sm">Active</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==================== VUE CLIENTS ====================
export const ClientsView = ({ clients, setSelectedClient }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('recent');

    const filteredClients = clients.filter(client =>
        client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const sortedClients = [...filteredClients].sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return (a.name || '').localeCompare(b.name || '');
            case 'spending':
                return (b.total_spent || 0) - (a.total_spent || 0);
            case 'reservations':
                return (b.total_reservations || 0) - (a.total_reservations || 0);
            default:
                return new Date(b.last_reservation || 0) - new Date(a.last_reservation || 0);
        }
    });

    return (
        <div className="space-y-6">
            {/* En-tête */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white">Clients</h2>
                    <p className="text-slate-300">{clients.length} clients au total</p>
                </div>
            </div>

            {/* Filtres */}
            <div className="bg-slate-800/50 rounded-xl p-4 flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Rechercher un client..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-lg pl-10 pr-4 py-2"
                        />
                    </div>
                </div>

                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-4 py-2"
                >
                    <option value="recent">Plus récents</option>
                    <option value="name">Nom (A-Z)</option>
                    <option value="spending">Dépenses (élevées)</option>
                    <option value="reservations">Réservations (plus)</option>
                </select>
            </div>

            {/* Liste des clients */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedClients.map(client => (
                    <div
                        key={client.id}
                        onClick={() => setSelectedClient(client)}
                        className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-xl p-6 hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                    >
                        {/* Avatar et nom */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                                {client.name?.charAt(0) || 'C'}
                            </div>
                            <div className="flex-1">
                                <h3 className="text-white font-semibold text-lg">{client.name}</h3>
                                <div className="flex items-center gap-1 mt-1">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className={`h-3 w-3 text-amber-400 ${i < Math.floor(client.rating || 0) ? 'fill-current' : ''}`} />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Statistiques */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-300">Réservations</span>
                                <span className="text-white font-bold">{client.total_reservations || 0}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-300">Dépenses totales</span>
                                <span className="text-emerald-400 font-bold">{(client.total_spent || 0).toLocaleString()} DZD</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-300">Dernière réservation</span>
                                <span className="text-white">{client.last_reservation ? new Date(client.last_reservation).toLocaleDateString() : 'N/A'}</span>
                            </div>
                        </div>

                        {/* Contact */}
                        <div className="mt-4 pt-4 border-t border-slate-600/50 space-y-2">
                            <div className="flex items-center gap-2 text-sm text-slate-300">
                                <Mail className="h-4 w-4" />
                                <span className="truncate">{client.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-300">
                                <Phone className="h-4 w-4" />
                                <span>{client.phone}</span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-4 flex gap-2">
                            <button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2">
                                <MessageSquare className="h-4 w-4" />
                                Message
                            </button>
                            <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2">
                                <Eye className="h-4 w-4" />
                                Profil
                            </button>
                        </div>
                    </div>
                ))}

                {sortedClients.length === 0 && (
                    <div className="col-span-full text-center py-16 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                        <User className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                        <h3 className="text-white text-lg font-semibold mb-2">Aucun client trouvé</h3>
                        <p className="text-slate-400">Les clients apparaîtront ici après leur première réservation</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==================== VUE FINANCES ====================
export const FinancesView = ({ stats, analytics, revenueData, reservations }) => {
    const [period, setPeriod] = useState('month');
    const [showExportModal, setShowExportModal] = useState(false);

    const totalRevenue = stats.total_revenue || 0;
    const monthlyRevenue = stats.monthly_revenue || 0;
    const pendingPayments = stats.pending_payments || 0;
    const completedPayments = stats.completed_payments || 0;

    return (
        <div className="space-y-6">
            {/* En-tête */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white">Finances & Rapports</h2>
                    <p className="text-slate-300">Suivi des revenus et paiements</p>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={() => setShowExportModal(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Download className="h-4 w-4" />
                        Exporter
                    </button>
                    <button className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
                        <FileText className="h-4 w-4" />
                        Rapport
                    </button>
                </div>
            </div>

            {/* KPIs financiers */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-400/30 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-emerald-500/20 rounded-xl">
                            <DollarSign className="h-6 w-6 text-emerald-400" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <p className="text-3xl font-bold text-white">{totalRevenue.toLocaleString()} DZD</p>
                        <p className="text-slate-300 text-sm font-medium">Revenus totaux</p>
                        <p className="text-slate-400 text-xs">Depuis le début</p>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-400/30 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-blue-500/20 rounded-xl">
                            <TrendingUp className="h-6 w-6 text-blue-400" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <p className="text-3xl font-bold text-white">{monthlyRevenue.toLocaleString()} DZD</p>
                        <p className="text-slate-300 text-sm font-medium">Ce mois</p>
                        <p className="text-emerald-400 text-xs">+15% vs mois dernier</p>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-400/30 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-amber-500/20 rounded-xl">
                            <Clock className="h-6 w-6 text-amber-400" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <p className="text-3xl font-bold text-white">{pendingPayments.toLocaleString()} DZD</p>
                        <p className="text-slate-300 text-sm font-medium">En attente</p>
                        <p className="text-slate-400 text-xs">{stats.pending_count || 0} paiements</p>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-400/30 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-purple-500/20 rounded-xl">
                            <CheckCircle className="h-6 w-6 text-purple-400" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <p className="text-3xl font-bold text-white">{completedPayments.toLocaleString()} DZD</p>
                        <p className="text-slate-300 text-sm font-medium">Reçus</p>
                        <p className="text-slate-400 text-xs">{stats.completed_count || 0} paiements</p>
                    </div>
                </div>
            </div>

            {/* Transactions récentes */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-white">Transactions récentes</h3>
                        <p className="text-slate-300 text-sm">Derniers paiements</p>
                    </div>

                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-4 py-2"
                    >
                        <option value="week">Cette semaine</option>
                        <option value="month">Ce mois</option>
                        <option value="year">Cette année</option>
                    </select>
                </div>

                <div className="space-y-3">
                    {reservations.slice(0, 10).map(reservation => (
                        <div key={reservation.id} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl hover:bg-slate-700/50 transition-colors">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <h4 className="text-white font-medium">{reservation.client_name}</h4>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium
                    ${reservation.payment_status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                                            reservation.payment_status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                                'bg-red-500/20 text-red-400'
                                        }`}>
                                        {reservation.payment_status === 'completed' ? '✓ Payé' :
                                            reservation.payment_status === 'pending' ? '⏳ En attente' : '✗ Échoué'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-300">
                                    <span>{reservation.vehicle}</span>
                                    <span>•</span>
                                    <span>{new Date(reservation.created_at || Date.now()).toLocaleDateString()}</span>
                                </div>
                            </div>

                            <div className="text-right">
                                <p className="text-emerald-400 font-bold text-lg">{reservation.amount?.toLocaleString()} DZD</p>
                                <p className="text-slate-400 text-xs">{reservation.payment_method || 'Carte'}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Revenus par véhicule */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Revenus par véhicule</h3>

                <div className="space-y-4">
                    {(analytics.revenue_by_vehicle || []).slice(0, 5).map((item, index) => (
                        <div key={index} className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-white font-medium">{item.vehicle}</span>
                                <span className="text-emerald-400 font-bold">{item.revenue?.toLocaleString()} DZD</span>
                            </div>
                            <div className="w-full bg-slate-700/50 rounded-full h-2">
                                <div
                                    className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all"
                                    style={{ width: `${(item.revenue / (analytics.max_vehicle_revenue || 1)) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ==================== VUE PROFIL ====================
export const ProfileView = ({ agency, fetchAgencyData }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: agency?.name || '',
        email: agency?.email || '',
        phone: agency?.phone || '',
        address: agency?.address || '',
        city: agency?.city || '',
        description: agency?.description || ''
    });

    const handleSave = async () => {
        // TODO: Implement save logic
        setIsEditing(false);
        await fetchAgencyData();
    };

    return (
        <div className="space-y-6">
            {/* En-tête */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white">Profil de l'Agence</h2>
                    <p className="text-slate-300">Gérer les informations de votre agence</p>
                </div>

                <button
                    onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:scale-105 flex items-center gap-2"
                >
                    {isEditing ? (
                        <>
                            <Save className="h-4 w-4" />
                            Enregistrer
                        </>
                    ) : (
                        <>
                            <Edit className="h-4 w-4" />
                            Modifier
                        </>
                    )}
                </button>
            </div>

            {/* Informations générales */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Informations générales</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-white text-sm font-medium">Nom de l'agence</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3 disabled:opacity-50"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-white text-sm font-medium">Email de contact</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3 disabled:opacity-50"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-white text-sm font-medium">Téléphone</label>
                        <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3 disabled:opacity-50"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-white text-sm font-medium">Ville</label>
                        <input
                            type="text"
                            value={formData.city}
                            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3 disabled:opacity-50"
                        />
                    </div>

                    <div className="md:col-span-2 space-y-2">
                        <label className="text-white text-sm font-medium">Adresse</label>
                        <input
                            type="text"
                            value={formData.address}
                            onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3 disabled:opacity-50"
                        />
                    </div>

                    <div className="md:col-span-2 space-y-2">
                        <label className="text-white text-sm font-medium">Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            disabled={!isEditing}
                            rows={4}
                            className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3 disabled:opacity-50"
                        />
                    </div>
                </div>
            </div>

            {/* Logo */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Logo de l'agence</h3>

                <div className="flex items-center gap-6">
                    <div className="w-32 h-32 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center text-white text-4xl font-bold">
                        {agency?.name?.charAt(0) || 'A'}
                    </div>

                    <div className="flex-1">
                        <p className="text-slate-300 mb-4">Téléchargez le logo de votre agence (PNG, JPG, max 2MB)</p>
                        <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2">
                            <Upload className="h-4 w-4" />
                            Télécharger un logo
                        </button>
                    </div>
                </div>
            </div>

            {/* Paramètres */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Paramètres de location</h3>

                <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                        <div>
                            <h4 className="text-white font-medium">Accepter les réservations automatiquement</h4>
                            <p className="text-slate-400 text-sm">Les réservations seront confirmées sans validation manuelle</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" />
                            <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                        </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                        <div>
                            <h4 className="text-white font-medium">Notifications par email</h4>
                            <p className="text-slate-400 text-sm">Recevoir un email pour chaque nouvelle réservation</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" defaultChecked className="sr-only peer" />
                            <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==================== VUE NOTIFICATIONS ====================
export const NotificationsView = ({ notifications, setUnreadCount }) => {
    const [filter, setFilter] = useState('all');

    const filteredNotifications = filter === 'all'
        ? notifications
        : filter === 'unread'
            ? notifications.filter(n => !n.read)
            : notifications.filter(n => n.read);

    const markAsRead = (notificationId) => {
        // TODO: Implement mark as read logic
        setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const markAllAsRead = () => {
        // TODO: Implement mark all as read logic
        setUnreadCount(0);
    };

    return (
        <div className="space-y-6">
            {/* En-tête */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white">Notifications</h2>
                    <p className="text-slate-300">{notifications.filter(n => !n.read).length} non lues</p>
                </div>

                <button
                    onClick={markAllAsRead}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:scale-105 flex items-center gap-2"
                >
                    <CheckCircle className="h-4 w-4" />
                    Tout marquer comme lu
                </button>
            </div>

            {/* Filtres */}
            <div className="flex space-x-2">
                <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all
            ${filter === 'all' ? 'bg-indigo-600 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'}`}
                >
                    Toutes ({notifications.length})
                </button>
                <button
                    onClick={() => setFilter('unread')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all
            ${filter === 'unread' ? 'bg-indigo-600 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'}`}
                >
                    Non lues ({notifications.filter(n => !n.read).length})
                </button>
                <button
                    onClick={() => setFilter('read')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all
            ${filter === 'read' ? 'bg-indigo-600 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'}`}
                >
                    Lues ({notifications.filter(n => n.read).length})
                </button>
            </div>

            {/* Liste des notifications */}
            <div className="space-y-3">
                {filteredNotifications.map(notification => (
                    <div
                        key={notification.id}
                        onClick={() => !notification.read && markAsRead(notification.id)}
                        className={`bg-gradient-to-r from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-xl p-6 hover:shadow-xl transition-all cursor-pointer
              ${!notification.read ? 'border-l-4 border-l-indigo-500' : ''}`}
                    >
                        <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl
                ${notification.type === 'reservation' ? 'bg-blue-500/20' :
                                    notification.type === 'payment' ? 'bg-emerald-500/20' :
                                        notification.type === 'review' ? 'bg-amber-500/20' :
                                            'bg-slate-500/20'
                                }`}>
                                {notification.type === 'reservation' ? <CalendarIcon className="h-6 w-6 text-blue-400" /> :
                                    notification.type === 'payment' ? <DollarSign className="h-6 w-6 text-emerald-400" /> :
                                        notification.type === 'review' ? <Star className="h-6 w-6 text-amber-400" /> :
                                            <Bell className="h-6 w-6 text-slate-400" />}
                            </div>

                            <div className="flex-1">
                                <div className="flex items-start justify-between gap-4 mb-2">
                                    <h3 className="text-white font-semibold">{notification.title}</h3>
                                    <span className="text-slate-400 text-sm whitespace-nowrap">
                                        {new Date(notification.created_at || Date.now()).toLocaleDateString()}
                                    </span>
                                </div>
                                <p className="text-slate-300 text-sm">{notification.message}</p>

                                {notification.action && (
                                    <button className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                        {notification.action}
                                    </button>
                                )}
                            </div>

                            {!notification.read && (
                                <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                            )}
                        </div>
                    </div>
                ))}

                {filteredNotifications.length === 0 && (
                    <div className="text-center py-16 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                        <Bell className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                        <h3 className="text-white text-lg font-semibold mb-2">Aucune notification</h3>
                        <p className="text-slate-400">Vous êtes à jour !</p>
                    </div>
                )}
            </div>
        </div>
    );
};
